import FetchClient from "./FetchClient";
import { memoryCache } from "./MemoryCache";

// Configuration constants
export const getTrackerLimit = (accountType?: string, maxAlertsOverride?: number) => {
  const base = accountType === "basic" ? 3 : 10;
  return maxAlertsOverride && maxAlertsOverride > 0 ? Math.max(base, maxAlertsOverride) : base;
};

export interface ZephyrStore {
  storeNbr: string;
  storeAddress: string;
  inventory: number;
  backroom: number;
  storePrice: number;
  aisleLocation?: string;
}

export interface TrackedProduct {
  userId: string;
  upc: string;
  name: string;
  image: string;
  category: string;
  zipCode: string;
  storeId?: string; // Legacy field for backward compatibility
  targetPrice: number;
  currentPrice: number;
  msrp: number;
  active: boolean;
  createdAt: number;
  lastChecked: number;
  lastNotified?: number;
  url?: string;
  prices: number[];
  checkTimestamps: number[];
  currentStock: number;
  stockStatus: string;
  stockHistory: number[];
  stockCheckTimestamps: number[];
  available: number;
  zephyrStores: ZephyrStore[];
  actualUpc?: string;
  status?: "ok" | "error"; // Monitor check status
  statusError?: string; // Error message if status is error
  rules?: WatchRules;
}

export interface WatchRules {
  mode?: "price" | "deal" | "resale";
  minDiscount?: number;
  minProfit?: number;
  minRoi?: number;
  resalePrice?: number;
  marketplaceFeeRate?: number;
  shippingCost?: number;
  notifyBackInStock?: boolean;
}

export interface AddTrackerRequest {
  upc: string; // Can be UPC or SKU
  zipCode: string;
  targetPrice: number;
  productName?: string;
  productImage?: string;
  productCategory?: string;
  msrp?: number;
  currentPrice?: number;
  rules?: WatchRules;
}

export interface UpdateTrackerRequest {
  targetPrice?: number;
  active?: boolean;
  rules?: WatchRules;
}

export interface TrackingResponse {
  monitors: TrackedProduct[];
  total: number;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
}

// Get all tracked products for the current user
export async function getTrackedProducts(): Promise<TrackedProduct[]> {
  try {
    const response = await FetchClient.get<ApiResponse<TrackingResponse>>(
      "/monitors"
    );
    return response.data.monitors;
  } catch (error) {
    console.error("Error fetching tracked products:", error);
    throw error;
  }
}

// Add a new product to tracking
export async function addTracker(
  trackerData: AddTrackerRequest,
  accountType?: string,
  maxAlertsOverride?: number
): Promise<TrackedProduct> {
  try {
    const limit = getTrackerLimit(accountType, maxAlertsOverride);
    const currentTrackers = await getTrackedProducts();

    if (currentTrackers.length >= limit) {
      throw new Error(
        `You can only track up to ${limit} products. Please remove some trackers to add new ones.`
      );
    }

    const response = await FetchClient.post<ApiResponse<TrackedProduct>>(
      "/monitors",
      trackerData
    );

    // Invalidate cache for this product
    const cacheKey = `tracker:${trackerData.upc}`;
    memoryCache.set(cacheKey, response.data, 5 * 60 * 1000);

    // Invalidate tracked products list cache
    memoryCache.delete("tracked-products");

    return response.data;
  } catch (error) {
    console.error("Error adding tracker:", error);
    throw error;
  }
}

// Update an existing tracker
export async function updateTracker(
  trackerId: string,
  updateData: UpdateTrackerRequest
): Promise<TrackedProduct> {
  try {
    const response = await FetchClient.patch<ApiResponse<TrackedProduct>>(
      `/monitors/${trackerId}`,
      updateData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating tracker:", error);
    throw error;
  }
}

// Remove a tracker
export async function removeTracker(upc: string): Promise<void> {
  try {
    await FetchClient.delete<ApiResponse<null>>(`/monitors/${upc}`);

    // Invalidate cache for this product
    const cacheKey = `tracker:${upc}`;
    memoryCache.delete(cacheKey);

    // Invalidate tracked products list cache
    memoryCache.delete("tracked-products");
  } catch (error) {
    console.error("Error removing tracker:", error);
    throw error;
  }
}

// Check if a product is being tracked
export async function isProductTracked(upc: string): Promise<boolean> {
  try {
    const response = await FetchClient.get<ApiResponse<{ monitored: boolean }>>(
      `/monitors/check/${upc}`
    );
    return response.data.monitored;
  } catch (error) {
    console.error("Error checking if product is tracked:", error);
    return false;
  }
}

// Get tracker details for a specific product (with caching)
export async function getTrackerByUpc(
  upc: string
): Promise<TrackedProduct | null> {
  const cacheKey = `tracker:${upc}`;

  // Check cache first
  const cached = memoryCache.get<TrackedProduct | null>(cacheKey);
  if (cached !== null) {
    console.log(`Cache hit for tracker ${upc}`);
    return cached;
  }

  try {
    const encodedUpc = encodeURIComponent(upc);
    const response = await FetchClient.get<ApiResponse<TrackedProduct>>(
      `/monitors/${encodedUpc}`
    );

    // Cache the result for 5 minutes
    memoryCache.set(cacheKey, response.data, 5 * 60 * 1000);
    return response.data;
  } catch (error) {
    console.error("Error fetching tracker by UPC:", error);
    // Cache null result for 1 minute to avoid repeated failed requests
    memoryCache.set(cacheKey, null, 60 * 1000);
    return null;
  }
}

// Toggle tracker active status
export async function toggleTrackerStatus(
  trackerId: string,
  active: boolean
): Promise<TrackedProduct> {
  return updateTracker(trackerId, { active });
}

// Update tracker target price
export async function updateTrackerPrice(
  trackerId: string,
  targetPrice: number
): Promise<TrackedProduct> {
  return updateTracker(trackerId, { targetPrice });
}

// Manually update a tracker with fresh product data
export async function manuallyUpdateTracker(
  upc: string
): Promise<TrackedProduct> {
  try {
    const response = await FetchClient.post<ApiResponse<TrackedProduct>>(
      `/monitors/manually-update/${upc}`,
      {}
    );
    return response.data;
  } catch (error) {
    console.error("Error manually updating tracker:", error);
    throw error;
  }
}

// ===== RETAILER-AWARE FUNCTIONS =====

// Get all tracked products for a specific retailer
export async function getTrackedProductsWithRetailer(
  retailer: string = "walmart"
): Promise<TrackedProduct[]> {
  try {
    const response = await FetchClient.get<ApiResponse<TrackingResponse>>(
      `/${retailer}/monitors`
    );
    return response.data.monitors;
  } catch (error) {
    console.error(`Error fetching tracked products for ${retailer}:`, error);
    throw error;
  }
}

// Add a new product to tracking for a specific retailer
export async function addTrackerWithRetailer(
  trackerData: AddTrackerRequest,
  retailer: string = "walmart",
  accountType?: string,
  maxAlertsOverride?: number
): Promise<TrackedProduct> {
  try {
    const limit = getTrackerLimit(accountType, maxAlertsOverride);
    const currentTrackers = await getTrackedProductsWithRetailer(retailer);

    if (currentTrackers.length >= limit) {
      throw new Error(
        `You can only track up to ${limit} products. Please remove some trackers to add new ones.`
      );
    }

    // Prevent duplicate monitors
    const isDuplicate = currentTrackers.some(t => {
      const baseUpc = t.upc.split("#")[0];
      return t.upc === trackerData.upc || t.actualUpc === trackerData.upc || baseUpc === trackerData.upc;
    });
    if (isDuplicate) {
      throw Object.assign(new Error("You already have an alert for this product."), { status: 409 });
    }

    const response = await FetchClient.post<ApiResponse<TrackedProduct>>(
      `/${retailer}/monitors`,
      trackerData
    );

    // Invalidate cache for this product
    const cacheKey = `tracker:${trackerData.upc}:${retailer}`;
    memoryCache.set(cacheKey, response.data, 5 * 60 * 1000);

    // Invalidate tracked products list cache
    memoryCache.deletePattern(new RegExp(`^tracked-products:${retailer}`));

    return response.data;
  } catch (error) {
    console.error(`Error adding tracker for ${retailer}:`, error);
    throw error;
  }
}

// Update an existing tracker for a specific retailer
export async function updateTrackerWithRetailer(
  trackerId: string,
  updateData: UpdateTrackerRequest,
  retailer: string = "walmart"
): Promise<TrackedProduct> {
  try {
    const response = await FetchClient.patch<ApiResponse<TrackedProduct>>(
      `/${retailer}/monitors/${trackerId}`,
      updateData
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating tracker for ${retailer}:`, error);
    throw error;
  }
}

// Remove a tracker for a specific retailer
export async function removeTrackerWithRetailer(
  upc: string,
  retailer: string = "walmart"
): Promise<void> {
  try {
    await FetchClient.delete<ApiResponse<null>>(`/${retailer}/monitors/${upc}`);

    // Invalidate cache for this product
    const cacheKey = `tracker:${upc}:${retailer}`;
    memoryCache.delete(cacheKey);

    // Invalidate tracked products list cache
    memoryCache.deletePattern(new RegExp(`^tracked-products:${retailer}`));
  } catch (error) {
    console.error(`Error removing tracker for ${retailer}:`, error);
    throw error;
  }
}

// Check if a product is being tracked for a specific retailer
export async function isProductTrackedWithRetailer(
  upc: string,
  retailer: string = "walmart"
): Promise<boolean> {
  try {
    const encodedUpc = encodeURIComponent(upc);
    const response = await FetchClient.get<ApiResponse<{ monitored: boolean }>>(
      `/${retailer}/monitors/check/${encodedUpc}`
    );
    return response.data.monitored;
  } catch (error) {
    console.error(
      `Error checking if product is tracked for ${retailer}:`,
      error
    );
    return false;
  }
}

// Get tracker details for a specific product and retailer (with caching)
export async function getTrackerByUpcWithRetailer(
  upc: string,
  retailer: string = "walmart"
): Promise<TrackedProduct | null> {
  const cacheKey = `tracker:${upc}:${retailer}`;

  // Check cache first
  const cached = memoryCache.get<TrackedProduct | null>(cacheKey);
  if (cached !== null) {
    console.log(`Cache hit for tracker ${upc} for retailer ${retailer}`);
    return cached;
  }

  try {
    const encodedUpc = encodeURIComponent(upc);
    const response = await FetchClient.get<ApiResponse<TrackedProduct>>(
      `/${retailer}/monitors/${encodedUpc}`
    );

    // Cache the result for 5 minutes
    memoryCache.set(cacheKey, response.data, 5 * 60 * 1000);
    return response.data;
  } catch (error) {
    console.error(`Error fetching tracker by UPC for ${retailer}:`, error);
    // Cache null result for 1 minute to avoid repeated failed requests
    memoryCache.set(cacheKey, null, 60 * 1000);
    return null;
  }
}

// Manually update a tracker with fresh product data for a specific retailer
export async function manuallyUpdateTrackerWithRetailer(
  upc: string,
  retailer: string = "walmart"
): Promise<TrackedProduct> {
  try {
    // URL encode the UPC to handle special characters like # in UPC#StoreID format
    const encodedUpc = encodeURIComponent(upc);
    const response = await FetchClient.post<ApiResponse<TrackedProduct>>(
      `/${retailer}/monitors/${encodedUpc}/manually-update`,
      {}
    );
    return response.data;
  } catch (error) {
    console.error(`Error manually updating tracker for ${retailer}:`, error);
    throw error;
  }
}

// ===== NEW PRODUCT-BASED FUNCTIONS =====

// Get all trackers for a specific product (actual UPC) across all stores
export async function getTrackersByProduct(
  actualUpc: string,
  retailer: string = "walmart"
): Promise<TrackedProduct[]> {
  try {
    const encodedUpc = encodeURIComponent(actualUpc);
    const response = await FetchClient.get<ApiResponse<TrackingResponse>>(
      `/${retailer}/monitors/by-product/${encodedUpc}`
    );
    return response.data.monitors;
  } catch (error) {
    console.error(`Error fetching trackers by product for ${retailer}:`, error);
    throw error;
  }
}

// Delete all trackers for a specific product (actual UPC) across all stores
export async function deleteTrackersByProduct(
  actualUpc: string,
  retailer: string = "walmart"
): Promise<{ deleted: number }> {
  try {
    const response = await FetchClient.delete<
      ApiResponse<{ message: string; deleted: number }>
    >(`/${retailer}/monitors/by-product/${actualUpc}`);

    // Invalidate caches
    memoryCache.deletePattern(new RegExp(`^tracker:.*:${retailer}`));
    memoryCache.deletePattern(new RegExp(`^tracked-products:${retailer}`));

    return { deleted: response.data.deleted };
  } catch (error) {
    console.error(`Error deleting trackers by product for ${retailer}:`, error);
    throw error;
  }
}

// Helper function to extract actual UPC from sort key (for frontend use)
export function extractActualUPC(sortKey: string): string {
  const parts = sortKey.split("#");
  return parts[0];
}

// Helper function to group trackers by actual UPC
export function groupTrackersByUPC(trackers: TrackedProduct[]): {
  [key: string]: TrackedProduct[];
} {
  const grouped: { [key: string]: TrackedProduct[] } = {};

  trackers.forEach((tracker) => {
    const actualUPC = extractActualUPC(tracker.upc);
    if (!grouped[actualUPC]) {
      grouped[actualUPC] = [];
    }
    grouped[actualUPC].push({
      ...tracker,
      actualUpc: actualUPC,
    });
  });

  return grouped;
}

// ===== STORE-AWARE TRACKING FUNCTIONS =====

// Check if a specific UPC+Store combination is being tracked
export async function isProductTrackedInStore(
  upc: string,
  storeId: string,
  retailer: string = "walmart"
): Promise<boolean> {
  try {
    // First check if there's a new format tracker (UPC#StoreID)
    const newFormatKey = `${upc}#${storeId}`;
    const encodedNewFormatKey = encodeURIComponent(newFormatKey);
    const response = await FetchClient.get<
      ApiResponse<{ monitored: boolean; count: number }>
    >(`/${retailer}/monitors/check/${encodedNewFormatKey}`);

    if (response.data.monitored) {
      return true;
    }

    // If not found in new format, check legacy format (UPC only) and verify store matches
    const encodedUpc = encodeURIComponent(upc);
    const legacyResponse = await FetchClient.get<
      ApiResponse<{ monitored: boolean; count: number }>
    >(`/${retailer}/monitors/check/${encodedUpc}`);

    if (legacyResponse.data.monitored) {
      return true; // Product is monitored
    }

    return false;
  } catch (error) {
    console.error(
      `Error checking if product is tracked in store for ${retailer}:`,
      error
    );
    return false;
  }
}

// Get tracker for a specific UPC+Store combination
export async function getTrackerByUpcAndStore(
  upc: string,
  storeId: string,
  retailer: string = "walmart"
): Promise<TrackedProduct | null> {
  try {
    // First try new format (UPC#StoreID)
    const newFormatKey = `${upc}#${storeId}`;
    try {
      const encodedNewFormatKey = encodeURIComponent(newFormatKey);
      const response = await FetchClient.get<ApiResponse<TrackedProduct>>(
        `/${retailer}/monitors/${encodedNewFormatKey}`
      );
      return response.data;
    } catch (newFormatError) {
      // If new format fails, try legacy format and verify store
      try {
        const tracker = await getTrackerByUpcWithRetailer(upc, retailer);
        if (tracker) {
          return tracker;
        }
      } catch (legacyError) {
        // Both formats failed
      }
    }

    return null;
  } catch (error) {
    console.error(
      `Error fetching tracker by UPC and store for ${retailer}:`,
      error
    );
    return null;
  }
}

// Remove tracker for a specific UPC+Store combination
export async function removeTrackerByUpcAndStore(
  upc: string,
  storeId: string,
  retailer: string = "walmart"
): Promise<void> {
  try {
    // First try new format (UPC#StoreID)
    const newFormatKey = `${upc}#${storeId}`;
    try {
      await FetchClient.delete<ApiResponse<null>>(
        `/${retailer}/monitors/${newFormatKey}`
      );

      // Invalidate caches
      const cacheKey = `tracker:${newFormatKey}:${retailer}`;
      memoryCache.delete(cacheKey);
      memoryCache.deletePattern(new RegExp(`^tracked-products:${retailer}`));
      return;
    } catch (newFormatError) {
      // If new format fails, try legacy format but verify store first
      const tracker = await getTrackerByUpcWithRetailer(upc, retailer);
      if (tracker) {
        await removeTrackerWithRetailer(upc, retailer);
        return;
      }
      throw new Error(`No tracker found for UPC ${upc}`);
    }
  } catch (error) {
    console.error(
      `Error removing tracker by UPC and store for ${retailer}:`,
      error
    );
    throw error;
  }
}
